import { formatTime } from '../../utils/locale';
import { Clock, Phone, CreditCard, Zap, Coffee, Activity, Ghost } from 'lucide-react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import { typedApi } from '../../api/typed';
import { useResource } from '../../hooks/useResource';

const BLOCK_COLOR = {
  TASK: 'var(--pd-focus)', CALL: 'var(--pd-blue)', PAYMENT: 'var(--pd-success)',
  FOCUS: 'var(--pd-warning)', BUFFER: 'var(--pd-subtle)', RITUAL: 'var(--pd-focus)',
  BREAK: 'var(--pd-success)', MEETING: 'var(--pd-warning)',
};
const BLOCK_ICON = { CALL: Phone, PAYMENT: CreditCard };

const ENERGY_ZONES = {
  peak: { label: 'Peak', color: 'var(--pd-warning)', icon: Zap },
  recovery: { label: 'Recovery', color: 'var(--pd-success)', icon: Coffee },
  steady: { label: 'Steady', color: 'var(--pd-focus)', icon: Activity },
};

export default function TimeExecutionRail({ blocks, date, onBlockClick, onShiftBlock, getBusinessColor, getBusinessShortCode }) {
  const { nowInTz, todayDateStrInTz } = useTimezone();
  const now = nowInTz();
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const isTodays = (date || todayDateStrInTz()) === todayDateStrInTz();
  const { preferences } = usePreferences();
  const energyCurve = preferences.energyCurve || {};

  const dateKey = date || todayDateStrInTz();
  const { data: ghostsData } = useResource(
    ['time-execution-rail-ghosts', dateKey],
    async ({ signal }) => await typedApi.get('/api/ghost-blocks', { params: { date: dateKey }, signal }),
    { initialData: { ghosts: [] } },
  );
  const ghostBlocks = ghostsData?.ghosts || [];

  const sorted = [...blocks].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

  const toMin = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };

  const totalScheduled = sorted.reduce((s, b) => s + Math.max(0, toMin(b.endTime) - toMin(b.startTime)), 0);
  const remaining = Math.max(0, 20 * 60 - Math.max(currentMin, 8 * 60));

  const currentBlock = isTodays ? sorted.find(b => currentMin >= toMin(b.startTime) && currentMin < toMin(b.endTime)) : null;
  const nextBlock = isTodays ? sorted.find(b => toMin(b.startTime) > currentMin) : null;

  // Determine current energy zone
  const getEnergyZone = (minute) => {
    for (const [zone, config] of Object.entries(energyCurve)) {
      if (config?.start && config?.end) {
        const s = toMin(config.start);
        const e = toMin(config.end);
        if (minute >= s && minute < e) return zone;
      }
    }
    return null;
  };

  const currentZone = isTodays ? getEnergyZone(currentMin) : null;
  const zoneConfig = currentZone ? ENERGY_ZONES[currentZone] : null;

  return (
    <div data-testid="time-execution-rail" className="flex flex-col h-full overflow-hidden"
      style={{ background: 'var(--pd-panel)', borderLeft: '1px solid var(--pd-border)' }}>

      <div className="px-4 pt-3 pb-2" style={{ borderBottom: '1px solid var(--pd-border-subtle)' }}>
        <div className="text-[10px] uppercase tracking-[0.1em] font-semibold"
          style={{ color: 'var(--pd-subtle)' }}>Execution Rail</div>
        <div className="flex items-center gap-2 mt-1 text-[10px]" style={{ color: 'var(--pd-muted)' }}>
          <span className="tabular-nums">{sorted.length} blocks</span>
          <span>·</span>
          <span className="tabular-nums">{Math.floor(totalScheduled / 60)}h {totalScheduled % 60}m</span>
        </div>
      </div>

      {isTodays && (
        <div className="px-4 py-2 flex items-center gap-2"
          style={{ borderBottom: '1px solid var(--pd-border-subtle)', background: 'var(--pd-panel-2)' }}>
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--pd-crimson)' }} />
          <span className="text-[11px] font-semibold tabular-nums" style={{ color: 'var(--pd-text)' }}>
            {formatTime(now)}
          </span>
          {zoneConfig && (
            <span data-testid="energy-zone-indicator" className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
              style={{ background: `${zoneConfig.color}12`, color: zoneConfig.color }}>
              <zoneConfig.icon className="w-3 h-3" />{zoneConfig.label}
            </span>
          )}
          {!zoneConfig && currentBlock && (
            <span className="text-[10px] truncate" style={{ color: 'var(--pd-muted)' }}>
              In: {currentBlock.title}
            </span>
          )}
          {!zoneConfig && !currentBlock && nextBlock && (
            <span className="text-[10px] truncate" style={{ color: 'var(--pd-subtle)' }}>
              Next: {nextBlock.title} at {formatTime(nextBlock.startTime)}
            </span>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {sorted.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="pd-empty-icon w-12 h-12 rounded-2xl mx-auto flex items-center justify-center mb-3"
              style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border-subtle)' }}>
              <Clock className="w-5 h-5" style={{ color: 'var(--pd-subtle)' }} />
            </div>
            <p className="text-[12px] font-medium" style={{ color: 'var(--pd-muted)' }}>No blocks scheduled</p>
            <p className="text-[10px] mt-1.5 leading-relaxed max-w-[160px] mx-auto" style={{ color: 'var(--pd-subtle)' }}>
              Use "Plan my day" to auto-schedule, or slot tasks from the queue
            </p>
          </div>
        ) : sorted.map((block, idx) => {
          const bStart = toMin(block.startTime);
          const bEnd = toMin(block.endTime);
          const dur = Math.max(0, bEnd - bStart);
          const isPast = isTodays && bEnd <= currentMin;
          const isCurrent = isTodays && bStart <= currentMin && bEnd > currentMin;
          const progress = isCurrent ? Math.min(100, ((currentMin - bStart) / dur) * 100) : isPast ? 100 : 0;
          const blockColor = BLOCK_COLOR[block.blockType] || 'var(--pd-focus)';
          const bizColor = getBusinessColor?.(block.businessId) || blockColor;
          const bizCode = getBusinessShortCode?.(block.businessId);
          const BlockIcon = BLOCK_ICON[block.blockType];
          const blockZone = getEnergyZone(bStart);
          const blockZoneConfig = blockZone ? ENERGY_ZONES[blockZone] : null;

          let gap = 0;
          if (idx > 0) {
            gap = bStart - toMin(sorted[idx - 1].endTime);
          }

          return (
            <div key={block.id}>
              {gap > 0 && (
                <div className="px-4 py-1 flex items-center gap-2" style={{ color: 'var(--pd-subtle)' }}>
                  <div className="flex-1 border-t border-dashed" style={{ borderColor: 'var(--pd-border-subtle)' }} />
                  <span className="text-[9px] tabular-nums">{gap}m</span>
                  <div className="flex-1 border-t border-dashed" style={{ borderColor: 'var(--pd-border-subtle)' }} />
                </div>
              )}
              <div data-testid={`rail-block-${block.id}`}
                className="px-4 py-2.5 cursor-pointer group transition-colors"
                style={{
                  borderBottom: '1px solid var(--pd-border-subtle)',
                  borderLeft: blockZoneConfig ? `2px solid ${blockZoneConfig.color}` : 'none',
                  opacity: isPast ? 0.5 : 1,
                  background: isCurrent ? `${bizColor}08` : 'transparent',
                }}
                onClick={() => onBlockClick?.(block)}
                onMouseEnter={(e) => { if (!isPast) e.currentTarget.style.background = 'var(--pd-panel-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isCurrent ? `${bizColor}08` : 'transparent'; }}>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] tabular-nums font-medium w-14 flex-shrink-0"
                    style={{ color: isCurrent ? 'var(--pd-text)' : 'var(--pd-subtle)' }}>
                    {formatTime(block.startTime)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {BlockIcon && <BlockIcon className="w-3 h-3 flex-shrink-0" style={{ color: blockColor }} />}
                      <span className="text-[11px] font-medium truncate"
                        style={{
                          color: isPast ? 'var(--pd-muted)' : 'var(--pd-text)',
                          textDecoration: isPast ? 'line-through' : 'none',
                        }}>
                        {block.title}
                      </span>
                    </div>
                    {(isCurrent || isPast) && (
                      <div className="h-1 rounded-full mt-1 overflow-hidden" style={{ background: 'var(--pd-panel-2)' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: bizColor }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {bizCode && (
                      <span className="text-[8px] px-1 py-[1px] rounded font-semibold"
                        style={{ background: `${bizColor}15`, color: bizColor }}>{bizCode}</span>
                    )}
                    <span className="text-[9px] tabular-nums" style={{ color: 'var(--pd-subtle)' }}>{dur}m</span>
                  </div>
                </div>
                {onShiftBlock && (
                  <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`rail-earlier-${block.id}`}
                      onClick={(e) => { e.stopPropagation(); onShiftBlock(block, -30); }}
                      className="text-[9px] px-1.5 py-0.5 rounded btn-ghost">Earlier</button>
                    <button data-testid={`rail-later-${block.id}`}
                      onClick={(e) => { e.stopPropagation(); onShiftBlock(block, 30); }}
                      className="text-[9px] px-1.5 py-0.5 rounded btn-ghost">Later</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Ghost Blocks Section */}
      {ghostBlocks.length > 0 && (
        <div data-testid="ghost-blocks-section" style={{ borderTop: '1px solid var(--pd-border-subtle)' }}>
          <div className="px-4 py-2 flex items-center gap-1.5">
            <Ghost className="w-3.5 h-3.5" style={{ color: 'var(--pd-crimson)' }} />
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-crimson)' }}>
              Ghost blocks ({ghostBlocks.length})
            </span>
          </div>
          {ghostBlocks.map((ghost, gIdx) => {
            const dur = Math.max(0, toMin(ghost.endTime) - toMin(ghost.startTime));
            return (
              <div key={ghost.id || `ghost-${gIdx}`} data-testid={`ghost-block-${ghost.id || gIdx}`}
                className="px-4 py-2 flex items-center gap-2"
                style={{ borderBottom: '1px dashed var(--pd-border-subtle)', opacity: 0.45 }}>
                <span className="text-[10px] tabular-nums w-14 flex-shrink-0" style={{ color: 'var(--pd-crimson)' }}>
                  {formatTime(ghost.startTime)}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] line-through truncate block" style={{ color: 'var(--pd-muted)' }}>
                    {ghost.title}
                  </span>
                  <span className="text-[9px]" style={{ color: 'var(--pd-crimson)' }}>
                    {ghost.reason}
                  </span>
                </div>
                <span className="text-[9px] tabular-nums" style={{ color: 'var(--pd-subtle)' }}>{dur}m</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Energy zone legend */}
      <div className="px-4 py-2" style={{ borderTop: '1px solid var(--pd-border-subtle)' }}>
        <div className="flex items-center gap-3">
          {Object.entries(energyCurve).map(([zone, config]) => {
            if (!config?.start) return null;
            const zc = ENERGY_ZONES[zone];
            if (!zc) return null;
            return (
              <div key={zone} className="flex items-center gap-1" data-testid={`energy-legend-${zone}`}>
                <div className="w-1.5 h-3 rounded-sm" style={{ background: zc.color }} />
                <span className="text-[8px] font-medium" style={{ color: 'var(--pd-subtle)' }}>
                  {zc.label} {formatTime(config.start)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2.5" style={{ borderTop: '1px solid var(--pd-border)', background: 'var(--pd-panel-2)' }}>
        <div className="flex items-center justify-between text-[10px]">
          <span style={{ color: 'var(--pd-subtle)' }}>Day remaining</span>
          <span className="tabular-nums font-medium" style={{ color: 'var(--pd-text)' }}>
            {Math.floor(remaining / 60)}h {remaining % 60}m
          </span>
        </div>
      </div>
    </div>
  );
}
